import { useEffect, useMemo, useState } from "react";

import clsx from "clsx";

import { useUser } from "@components/user-context";
import { usePrettyLogEventPage } from "@components/v2/analytics";
import {
  DataTypeProvider,
  IntegratedSelection,
  SelectionState,
} from "@devexpress/dx-react-grid";
import {
  Grid,
  TableColumnResizing,
  TableFixedColumns,
  TableHeaderRow,
  TableSelection,
  VirtualTable,
} from "@devexpress/dx-react-grid-bootstrap4";
import "@devexpress/dx-react-grid-bootstrap4/dist/dx-react-grid-bootstrap4.css";
import { useLandingPageAnalyticsContext } from "@features/landing-page-analytics/hooks";
import { GET_LANDING_PAGE_REPORT_TABLE } from "@gql/landing-page-analytics";
import { useNorthbeamQuery } from "@hooks/http";
import {
  type GetLandingPageReportTable,
  type GetLandingPageReportTableVariables,
} from "@north-beam/nb-client-side";
import { Paginator } from "@shared/paginator";

import { TableCell, TableRow, TableRowLoading } from "./body-components";
import {
  NAME_COLUMN_ID,
  ROW_HEIGHT,
  ROWS_PER_PAGE,
  SELECTION_COLUMN_WIDTH,
  URL_COLUMN_ID,
} from "./constants";
import {
  MetricDataFormatter,
  NameDataFormatter,
  UrlDataFormatter,
} from "./data-type-formatters";
import {
  TableHeaderCell,
  TableHeaderContent,
  TableHeadWithFixedRow,
  TableSelectionCell,
  TableSelectionHeaderCell,
} from "./header-components";
import { buildTableRows } from "./utils";

export const Table = () => {
  const prettyLogEventPage = usePrettyLogEventPage();
  const { user } = useUser();
  const {
    columns,
    tableState,
    reportTableVariables,
    setColumnConfigs,
    setSelectedRows,
    setPage,
  } = useLandingPageAnalyticsContext();
  const [columnWidths, setColumnWidths] = useState<
    { columnName: string; width: number }[]
  >([]);
  const [data, setData] = useState<GetLandingPageReportTable | undefined>();

  useEffect(() => {
    setColumnWidths(
      columns.map(({ name, width }) => ({
        columnName: name,
        width: width,
      })),
    );
  }, [columns]);

  const { loading } = useNorthbeamQuery<
    GetLandingPageReportTable,
    GetLandingPageReportTableVariables
  >(GET_LANDING_PAGE_REPORT_TABLE, {
    variables: reportTableVariables,
    onCompleted(data) {
      setData(data);
    },
  });

  const { data: summaryData, loading: summaryLoading } = useNorthbeamQuery<
    GetLandingPageReportTable,
    GetLandingPageReportTableVariables
  >(GET_LANDING_PAGE_REPORT_TABLE, {
    variables: {
      ...reportTableVariables,
      sorting: undefined,
      page: undefined,
      perPage: undefined,
      aggregateAll: true,
    },
  });

  const rows = useMemo(() => {
    const dataRows = data?.me.landingPageReportTable.rows;

    if (!dataRows) {
      return [];
    }

    return buildTableRows(columns, dataRows, user);
  }, [user, data, columns]);

  const summaryRow = useMemo(() => {
    const dataRows = summaryData?.me.landingPageReportTable.rows;
    const emptyRow = {
      id: "",
      index: 0,
      adKey: "",
      name: "",
      landingPage: null,
      campaignName: null,
      adsetName: null,
      adName: null,
      metrics: {
        rev: "",
        revFt: "",
        txns: "",
        txnsFt: "",
        visits: "",
        newVisits: "",
      },
    };

    if (!dataRows) {
      return emptyRow;
    }

    return buildTableRows(columns, dataRows, user)[0] || emptyRow;
  }, [user, summaryData, columns]);

  const possibleLoadingRows = useMemo(() => {
    if (!data) {
      return Array.from({ length: 20 }).map((_, index) => {
        return {
          id: `loading-row-${index}`,
          index,
          adKey: "",
          landingPage: "",
          name: "",
          campaignName: null,
          adsetName: null,
          adName: null,
          metrics: {},
        };
      });
    } else {
      return rows;
    }
  }, [columns, data, rows]);

  const FixedTopRow = useMemo(
    () => TableHeadWithFixedRow(summaryRow, summaryLoading),
    [summaryLoading, summaryRow],
  );

  return (
    <div className="px-6 py-4 text-xs">
      <div
        className={clsx(
          "sales-table-v3 sales-table-v3-compact-row-height h-[35rem]",
          "border border-gray bg-white rounded-lg overflow-hidden relative text-xs",
        )}
      >
        <Grid
          columns={columns}
          rows={possibleLoadingRows}
          getRowId={(row) => row.id}
        >
          <SelectionState
            selection={tableState.selectedRows}
            onSelectionChange={(val) =>
              setSelectedRows(val.map((e) => String(e)))
            }
          />
          <IntegratedSelection />
          <VirtualTable
            height="auto"
            estimatedRowHeight={ROW_HEIGHT}
            headComponent={FixedTopRow as React.ComponentType<object>}
            rowComponent={(props) =>
              loading ? (
                <TableRowLoading columns={columns} row={props.row} />
              ) : (
                <TableRow {...props}></TableRow>
              )
            }
            cellComponent={TableCell}
          />
          <TableColumnResizing
            columnWidths={columnWidths}
            onColumnWidthsChange={(values) => {
              prettyLogEventPage({
                domain: "Landing Page Analytics",
                action: "Other",
                additionalActionInfo: "Change Column Widths",
                data: {
                  newColumnWidths: values,
                  pageContext: { columnWidths },
                },
              });

              setColumnWidths(
                values.map((e) => ({
                  columnName: e.columnName,
                  width: Number(e.width),
                })),
              );
              setColumnConfigs(
                values.map((e) => ({
                  name: e.columnName,
                  width: Number(e.width),
                })),
              );
            }}
          />
          <TableHeaderRow
            cellComponent={TableHeaderCell}
            contentComponent={TableHeaderContent}
          />
          <TableSelection
            showSelectAll
            selectionColumnWidth={SELECTION_COLUMN_WIDTH}
            headerCellComponent={TableSelectionHeaderCell}
            cellComponent={TableSelectionCell}
          />
          <DataTypeProvider
            for={[URL_COLUMN_ID]}
            formatterComponent={UrlDataFormatter}
          />
          <DataTypeProvider
            for={[NAME_COLUMN_ID]}
            formatterComponent={NameDataFormatter}
          />
          <DataTypeProvider
            for={columns
              .map((e) => e.name)
              .filter(
                (name) => ![URL_COLUMN_ID, NAME_COLUMN_ID].includes(name),
              )}
            formatterComponent={MetricDataFormatter}
          />
          <TableFixedColumns
            leftColumns={[TableSelection.COLUMN_TYPE, URL_COLUMN_ID]}
          />
        </Grid>
      </div>
      <div className="flex items-center justify-end my-3">
        <Paginator
          page={tableState.page}
          perPage={ROWS_PER_PAGE}
          total={data?.me.landingPageReportTable.total ?? 0}
          onChange={setPage}
          disabled={loading}
        />
      </div>
    </div>
  );
};
